import React from "react";
import { useAppStore } from "@/core/store/useAppStore";
import { GridMode } from "./GridNavigation";
import { cn } from "../../utils/cn";
import { MinimalPostCard } from "@/domains/PostCreator/cards/MinimalPostCard";
import { ScheduleButton } from "@/domains/PostCreator/ScheduleButton";
import type { GridItem as GridItemType } from "./types/grid.types";

interface GridSlotProps {
  item: GridItemType;
  gridMode: GridMode;
  className?: string;
  onRemoveFromGrid?: (postId: string) => void;
}

const RemoveFromGridButton: React.FC<{
  onRemove: () => void;
}> = ({ onRemove }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onRemove();
    }}
    className="p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg"
    title="Remove from grid"
  >
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
);

export const GridSlot: React.FC<GridSlotProps> = ({
  item,
  className,
  onRemoveFromGrid,
}) => {
  const { ui } = useAppStore();

  const hasContent =
    item.post && item.post.mediaItems && item.post.mediaItems.length > 0;

  const gridActions = item.post ? (
    <>
      <ScheduleButton post={item.post} compact variant="grid" />
      {onRemoveFromGrid && (
        <RemoveFromGridButton onRemove={() => onRemoveFromGrid(item.post!.id)} />
      )}
    </>
  ) : null;

  return (
    <div
      className={cn(
        "group relative rounded-xl overflow-hidden transition-all duration-300 ease-in-out w-full h-full",
        // Empty state styling
        !hasContent && [
          "bg-gray-50/50 border-2 border-dashed border-gray-200",
          "cursor-pointer hover:border-gray-300 hover:bg-gray-50",
        ],
        // Content state styling
        hasContent && [
          "bg-transparent", // Let PostCard handle its own background
          ui.editMode && "drag-handle hover:shadow-xl hover:shadow-gray-100/50",
        ],
        className,
      )}
    >
      {hasContent && item.post ? (
        <MinimalPostCard post={item.post} actions={gridActions} />
      ) : (
        // Empty slot — the dashed outline alone is the drop affordance,
        // no icon or copy inside.
        <div className="w-full h-full" />
      )}

      {/* Position indicator in edit mode - more subtle */}
      {ui.editMode && (
        <div className="absolute top-2 left-2 w-7 h-7 bg-white border border-gray-200 text-gray-600 rounded-full flex items-center justify-center text-xs font-semibold shadow-xs z-10">
          {item.position + 1}
        </div>
      )}
    </div>
  );
};
